"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Table,
  Badge,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap"
import {
  FaChartBar,
  FaCalendarCheck,
  FaCalendarTimes,
  FaCalendarPlus,
  FaClock,
  FaHistory,
  FaEye,
  FaInfoCircle,
} from "react-icons/fa"
import { Btn } from "../../../AbstractElements"
import {ExhibitionStatisticsService} from "../../../Service/ExhibitionStatisticsService"
import type { ExhibitionStatistics, Exhibition } from "./types"
import { toast } from "react-toastify"

interface Props {
  refreshTrigger: boolean
  onRefresh: () => void
}

const ExhibitionStatisticsList: React.FC<Props> = ({ refreshTrigger, onRefresh }) => {
  const navigate = useNavigate()
  const [statistics, setStatistics] = useState<ExhibitionStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("1")

  // Fetch statistics with lists
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true)
        const data = await ExhibitionStatisticsService.getStatistics()
        setStatistics(data)
      } catch (error) {
        console.error("Failed to fetch statistics:", error)
        toast.error("Failed to load exhibition statistics")
      } finally {
        setLoading(false)
      }
    }

    fetchStatistics()
  }, [refreshTrigger])

  const handleViewDetails = (exhibition: Exhibition) => {
    navigate(`${process.env.PUBLIC_URL}/exhibition/${exhibition.id}/related`)
  }

  const toggle = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  // Get current exhibitions based on active tab
  const getCurrentExhibitions = (): Exhibition[] => {
    if (!statistics) return []

    switch (activeTab) {
      case "1":
        return statistics.active_exhibitions_list
      case "2":
        return statistics.inactive_exhibitions_list
      case "3":
        return statistics.upcoming_exhibitions_list
      case "4":
        return statistics.ongoing_exhibitions_list
      case "5":
        return statistics.past_exhibitions_list
      default:
        return []
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  if (!statistics) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4 className="text-muted">Failed to load statistics</h4>
          <button className="btn btn-primary mt-3" onClick={onRefresh}>
            <i className="fa fa-refresh me-2"></i>
            Retry
          </button>
        </div>
      </div>
    )
  }

  const currentExhibitions = getCurrentExhibitions()

  return (
    <div>
      {/* Enhanced Statistics Cards */}
      <Row className="mb-4">
        <Col xl="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="text-center p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="bg-dark bg-opacity-10 rounded-circle p-3 me-3">
                      <FaChartBar size={24} className="text-primary" />
                    </div>
                    <FaInfoCircle id="total-popover" size={14} className="text-muted" style={{ cursor: "pointer" }} />
                  </div>
                  <h2 className="mb-1 fw-bold text-primary">{statistics.total_exhibitions}</h2>
                  <p className="mb-0 text-muted small fw-medium">Total Exhibitions</p>
                </div>
              </div>
              <div className="border-top border-primary border-2 pt-2">
                <small className="text-primary fw-medium">All Time</small>
              </div>
            </CardBody>
            <UncontrolledPopover placement="top" target="total-popover">
              <PopoverHeader className="bg-primary text-white">Total Exhibitions</PopoverHeader>
              <PopoverBody>All exhibitions in the system including active, inactive, and archived ones.</PopoverBody>
            </UncontrolledPopover>
          </Card>
        </Col>

        <Col xl="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="text-center p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                      <FaCalendarCheck size={24} className="text-success" />
                    </div>
                    <FaInfoCircle id="active-popover" size={14} className="text-muted" style={{ cursor: "pointer" }} />
                  </div>
                  <h2 className="mb-1 fw-bold text-success">{statistics.active_exhibitions}</h2>
                  <p className="mb-0 text-muted small fw-medium">Active</p>
                </div>
              </div>
              <div className="border-top border-success border-2 pt-2">
                <small className="text-success fw-medium">Currently Running</small>
              </div>
            </CardBody>
            <UncontrolledPopover placement="top" target="active-popover">
              <PopoverHeader className="bg-success text-white">Active Exhibitions</PopoverHeader>
              <PopoverBody>Exhibitions that are currently active and available for participation.</PopoverBody>
            </UncontrolledPopover>
          </Card>
        </Col>

        <Col xl="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="text-center p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="bg-secondary bg-opacity-10 rounded-circle p-3 me-3">
                      <FaCalendarTimes size={24} className="text-secondary" />
                    </div>
                    <FaInfoCircle
                      id="inactive-popover"
                      size={14}
                      className="text-muted"
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                  <h2 className="mb-1 fw-bold text-secondary">{statistics.inactive_exhibitions}</h2>
                  <p className="mb-0 text-muted small fw-medium">Inactive</p>
                </div>
              </div>
              <div className="border-top border-secondary border-2 pt-2">
                <small className="text-secondary fw-medium">Disabled</small>
              </div>
            </CardBody>
            <UncontrolledPopover placement="top" target="inactive-popover">
              <PopoverHeader className="bg-secondary text-white">Inactive Exhibitions</PopoverHeader>
              <PopoverBody>Exhibitions that are currently inactive or disabled.</PopoverBody>
            </UncontrolledPopover>
          </Card>
        </Col>

        <Col xl="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="text-center p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                      <FaCalendarPlus size={24} className="text-info" />
                    </div>
                    <FaInfoCircle
                      id="upcoming-popover"
                      size={14}
                      className="text-muted"
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                  <h2 className="mb-1 fw-bold text-info">{statistics.upcoming_exhibitions}</h2>
                  <p className="mb-0 text-muted small fw-medium">Upcoming</p>
                </div>
              </div>
              <div className="border-top border-info border-2 pt-2">
                <small className="text-info fw-medium">Future Events</small>
              </div>
            </CardBody>
            <UncontrolledPopover placement="top" target="upcoming-popover">
              <PopoverHeader className="bg-info text-white">Upcoming Exhibitions</PopoverHeader>
              <PopoverBody>Exhibitions scheduled to start in the future.</PopoverBody>
            </UncontrolledPopover>
          </Card>
        </Col>

        <Col xl="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="text-center p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                      <FaClock size={24} className="text-warning" />
                    </div>
                    <FaInfoCircle id="ongoing-popover" size={14} className="text-muted" style={{ cursor: "pointer" }} />
                  </div>
                  <h2 className="mb-1 fw-bold text-warning">{statistics.ongoing_exhibitions}</h2>
                  <p className="mb-0 text-muted small fw-medium">Ongoing</p>
                </div>
              </div>
              <div className="border-top border-warning border-2 pt-2">
                <small className="text-warning fw-medium">In Progress</small>
              </div>
            </CardBody>
            <UncontrolledPopover placement="top" target="ongoing-popover">
              <PopoverHeader className="bg-warning text-white">Ongoing Exhibitions</PopoverHeader>
              <PopoverBody>Exhibitions that are currently running and in progress.</PopoverBody>
            </UncontrolledPopover>
          </Card>
        </Col>

        <Col xl="2" md="4" sm="6" className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="text-center p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="bg-dark bg-opacity-10 rounded-circle p-3 me-3">
                      <FaHistory size={24} className="text-dark" />
                    </div>
                    <FaInfoCircle id="past-popover" size={14} className="text-muted" style={{ cursor: "pointer" }} />
                  </div>
                  <h2 className="mb-1 fw-bold text-dark">{statistics.past_exhibitions}</h2>
                  <p className="mb-0 text-muted small fw-medium">Past</p>
                </div>
              </div>
              <div className="border-top border-dark border-2 pt-2">
                <small className="text-dark fw-medium">Completed</small>
              </div>
            </CardBody>
            <UncontrolledPopover placement="top" target="past-popover">
              <PopoverHeader className="bg-dark text-white">Past Exhibitions</PopoverHeader>
              <PopoverBody>Exhibitions that have already ended and are completed.</PopoverBody>
            </UncontrolledPopover>
          </Card>
        </Col>
      </Row>

      {/* Simplified Border Tabs Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="bg-white border-0 pb-0">
          <Nav className="nav-tabs border-0">
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "1" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("1")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "1" ? "3px solid rgb(200, 175, 77)" : "3px solid transparent",
                }}
              >
                <FaCalendarCheck className={`me-2 ${activeTab === "1" ? "text-primary" : "text-muted"}`} />
                Active ({statistics.active_exhibitions})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "2" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("2")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "2" ? "3px solid #0d6efd" : "3px solid transparent",
                }}
              >
                <FaCalendarTimes className={`me-2 ${activeTab === "2" ? "text-primary" : "text-muted"}`} />
                Inactive ({statistics.inactive_exhibitions})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "3" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("3")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "3" ? "3px solid #0d6efd" : "3px solid transparent",
                }}
              >
                <FaCalendarPlus className={`me-2 ${activeTab === "3" ? "text-primary" : "text-muted"}`} />
                Upcoming ({statistics.upcoming_exhibitions})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "4" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("4")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "4" ? "3px solid #0d6efd" : "3px solid transparent",
                }}
              >
                <FaClock className={`me-2 ${activeTab === "4" ? "text-primary" : "text-muted"}`} />
                Ongoing ({statistics.ongoing_exhibitions})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`nav-link border-0 px-4 py-3 ${activeTab === "5" ? "active text-primary fw-bold" : "text-muted"}`}
                onClick={() => toggle("5")}
                style={{
                  cursor: "pointer",
                  borderBottom: activeTab === "5" ? "3px solid #0d6efd" : "3px solid transparent",
                }}
              >
                <FaHistory className={`me-2 ${activeTab === "5" ? "text-primary" : "text-muted"}`} />
                Past ({statistics.past_exhibitions})
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>
        <CardBody className="pt-4">
          <TabContent activeTab={activeTab}>
            {["1", "2", "3", "4", "5"].map((tabId) => (
              <TabPane key={tabId} tabId={tabId}>
                <div className="table-responsive">
                  <Table hover>
                    <thead className="table-light">
                      <tr>
                        <th className="fw-semibold">Title</th>
                        <th className="fw-semibold">Type</th>
                        <th className="fw-semibold">Venue</th>
                        <th className="fw-semibold">Start Date</th>
                        <th className="fw-semibold">End Date</th>
                        <th className="fw-semibold">Status</th>
                        <th className="fw-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentExhibitions.length > 0 ? (
                        currentExhibitions.map((exhibition) => (
                          <tr key={exhibition.id}>
                            <td className="fw-medium">{exhibition.title}</td>
                            <td>
                              <span className="fw-medium">{exhibition.exhibition_type_name}</span>
                            </td>
                            <td className="text-muted">{exhibition.venue}</td>
                            <td className="text-muted">{exhibition.start_date}</td>
                            <td className="text-muted">{exhibition.end_date}</td>
                            <td>
                              <Badge color={exhibition.status ? "success" : "secondary"} pill className="px-3 py-2">
                                {exhibition.status ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>
                              <Btn color="dark" size="xs" onClick={() => handleViewDetails(exhibition)} title="View Details">
                                          <FaEye size={12} />
                                </Btn>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-5">
                            <div className="text-muted">
                              <i className="fa fa-inbox fa-2x mb-3 d-block"></i>
                              <h6>No exhibitions found</h6>
                              <p className="mb-0">There are no exhibitions in this category.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </TabPane>
            ))}
          </TabContent>
        </CardBody>
      </Card>
    </div>
  )
}

export default ExhibitionStatisticsList
